import { useAppDispatch, useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import { deleteStructureImage } from 'features/data';
import { suggestSpacesForLevel } from 'features/structure';
import { useAssignLevelLayoutImage } from 'hooks/use-assign-level-layout-image';
import { useEffect, useRef, useState } from 'react';
import ReactCrop, {
  convertToPixelCrop,
  Crop,
  PixelCrop
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import axios from 'utils/api';
import { loadStructureAttachments } from 'utils/data';
import { makeCenteredSquareCrop } from 'utils/default-image-crop';
import { errorModal } from 'utils/ui';

type CropImageModalProps = {
  openState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  attachmentId: number | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
  onSkip?: () => void;
  targetLevelUniqueId?: string | null;
};

export default function CropImageModal({
  openState,
  attachmentId,
  attachmentUrl,
  attachmentName,
  onSkip,
  targetLevelUniqueId
}: CropImageModalProps) {
  const dispatch = useAppDispatch();
  const { structureUniqueId, structureId, structureAttachments } =
    useAppSelector((state) => state.structure);
  const assignLevelLayoutImage = useAssignLevelLayoutImage();

  const [, setOpen] = openState;
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isSaving, setIsSaving] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const isAutoMode = !!onSkip;
  const isDragDropMode = !!targetLevelUniqueId;

  useEffect(() => {
    if (openState[0]) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setImageLoaded(false);
      setImageError(false);
    }
  }, [openState[0]]);

  useEffect(() => {
    if (!imageLoaded || !imgRef.current) {
      return;
    }
    const { width, height } = imgRef.current;
    if (!width || !height) {
      return;
    }
    const defaultCrop = makeCenteredSquareCrop(width, height);
    setCrop(defaultCrop);
    setCompletedCrop(convertToPixelCrop(defaultCrop, width, height));
  }, [imageLoaded]);

  const handleClose = () => {
    setCrop(undefined);
    setCompletedCrop(undefined);
    setImageLoaded(false);
    setImageError(false);
  };

  const assignCroppedImageToLevel = async (levelUniqueId: string) => {
    if (!structureId) return;
    const updatedAttachments = await loadStructureAttachments(structureId);
    if (!updatedAttachments) return;

    const existingIds = new Set(structureAttachments.map((a) => a.id));
    const newAttachment = updatedAttachments.find(
      (a) => !existingIds.has(a.id)
    );
    if (!newAttachment) return;

    await assignLevelLayoutImage(newAttachment.id, levelUniqueId);
    dispatch(suggestSpacesForLevel({ levelUniqueId }));
  };

  const handleSave = async () => {
    if (
      !imgRef.current ||
      !completedCrop ||
      !structureUniqueId ||
      !attachmentUrl
    ) {
      errorModal('Please select an area to crop');
      return;
    }

    if (completedCrop.width < 10 || completedCrop.height < 10) {
      errorModal('Crop area is too small');
      return;
    }

    setIsSaving(true);

    try {
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

      const cropData = {
        x: Math.round(completedCrop.x * scaleX),
        y: Math.round(completedCrop.y * scaleY),
        width: Math.round(completedCrop.width * scaleX),
        height: Math.round(completedCrop.height * scaleY)
      };

      await axios.post('structure/crop-structure-image', {
        attachmentId,
        structureUniqueId,
        cropData,
        newName: `${attachmentName || 'Image'} (Cropped)`
      });

      if (isDragDropMode && targetLevelUniqueId) {
        await assignCroppedImageToLevel(targetLevelUniqueId);
        setOpen(false);
      } else if (isAutoMode && attachmentId) {
        await dispatch(deleteStructureImage(attachmentId));
        if (structureId) {
          await loadStructureAttachments(structureId);
        }
        onSkip();
      } else {
        if (structureId) {
          await loadStructureAttachments(structureId);
        }
        setOpen(false);
      }
    } catch (error) {
      console.error('Error saving cropped image:', error);
      errorModal('Failed to save cropped image');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkipAndAssignOriginal = async () => {
    if (!targetLevelUniqueId || !attachmentId) return;
    await assignLevelLayoutImage(attachmentId, targetLevelUniqueId);
    dispatch(suggestSpacesForLevel({ levelUniqueId: targetLevelUniqueId }));
    setOpen(false);
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
      setOpen(false);
      return;
    }
    if (isDragDropMode && targetLevelUniqueId) {
      handleSkipAndAssignOriginal();
    }
  };

  if (!attachmentUrl) {
    return null;
  }

  const showInlineButtons = isAutoMode || isDragDropMode;

  return (
    <Modal
      title='Crop Image'
      openState={openState}
      onSave={showInlineButtons ? undefined : handleSave}
      onClose={handleClose}
      buttonText={isSaving ? 'Saving...' : 'Crop'}
      saveDisabled={isSaving || !completedCrop}
      closeOnSave={false}
      infoMode={showInlineButtons}
      size='wide'
    >
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-gray-600'>
          Crop the image to the footprint of the layout.
        </p>

        <div className='flex max-h-[500px] items-center justify-center overflow-auto bg-gray-100 p-2'>
          {imageError ? (
            <div className='flex h-48 items-center justify-center text-red-500'>
              Failed to load image
            </div>
          ) : (
            <>
              {!imageLoaded && (
                <div className='flex h-48 items-center justify-center text-gray-500'>
                  Loading image...
                </div>
              )}
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                style={{ display: imageLoaded ? 'block' : 'none' }}
              >
                <img
                  ref={imgRef}
                  src={attachmentUrl ?? ''}
                  alt='Crop preview'
                  style={{ maxHeight: '480px', maxWidth: '100%' }}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                />
              </ReactCrop>
            </>
          )}
        </div>

        {showInlineButtons && (
          <div className='flex gap-3'>
            <button
              type='button'
              className='flex-1 rounded-lg bg-gray-200 py-3 font-semibold text-gray-700 hover:bg-gray-300'
              onClick={handleSkip}
              disabled={isSaving}
            >
              {isDragDropMode && !onSkip ? 'Assign Without Cropping' : 'Skip'}
            </button>
            <button
              type='button'
              className='flex-1 rounded-lg bg-blue-500 py-3 font-semibold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400'
              onClick={handleSave}
              disabled={isSaving || !completedCrop}
            >
              {isSaving ? 'Saving...' : 'Crop & Assign'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
